import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Phone, MapPin, Mail, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES_OPTIONS = [
  "Instalação de Ar Condicionado",
  "Manutenção de Ar Condicionado",
  "Instalação de Câmara Fria",
  "Manutenção de Câmara Fria",
  "Refrigeração Hospitalar",
  "Sistema de Chiller",
  "Torre de Resfriamento",
  "Outro",
];

export default function Contact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ nome: "", telefone: "", email: "", servico: "", mensagem: "" });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefone || !form.servico) return;

    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        nome: form.nome,
        telefone: form.telefone,
        email: form.email,
        servico: form.servico,
        mensagem: form.mensagem,
      });
    } catch (err) {
      console.error("Error saving contact:", err);
    }

    // Store conversion data in sessionStorage (one-time token)
    sessionStorage.setItem("rl_conversion_token", Date.now().toString());
    sessionStorage.setItem("rl_lead_data", JSON.stringify(form));

    setSending(false);
    setForm({ nome: "", telefone: "", email: "", servico: "", mensagem: "" });

    // Navigate to thank you page (which fires conversion + opens WhatsApp)
    navigate("/obrigado");
  };

  return (
    <section
      id="contato"
      data-testid="contact-section"
      className="py-24 bg-[#0E86D4]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Info */}
          <div className="text-white">
            <span className="inline-block bg-white/15 backdrop-blur-sm border border-white/20 text-white text-xs font-bold tracking-[0.2em] uppercase px-4 py-1.5 font-['Barlow_Condensed']">
              Contato
            </span>
            <h2
              data-testid="contact-title"
              className="mt-4 text-3xl md:text-4xl font-extrabold font-['Barlow_Condensed'] uppercase tracking-tight"
            >
              Solicite Seu Orçamento
            </h2>
            <p className="mt-4 text-base text-white/80 leading-relaxed font-['Manrope']">
              Preencha o formulário e você será redirecionado para o nosso WhatsApp com sua mensagem pronta. 
              Responderemos o mais rápido possível!
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/15 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider">WhatsApp</p>
                  <p className="text-white/80 text-sm font-['Manrope']">(62) 98629-3474</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/15 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider">E-mail</p>
                  <p className="text-white/80 text-sm font-['Manrope']">rlrefrigeracaogoiania@gmail.com</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/15 rounded-sm flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider">Localização</p>
                  <p className="text-white/80 text-sm font-['Manrope']">Goiânia - GO</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/15 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider">Horário</p>
                  <p className="text-white/80 text-sm font-['Manrope']">Seg - Sáb: 08:00 - 18:00</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-white rounded-sm p-8 shadow-xl">
            <h3 className="text-xl font-bold text-[#0F172A] font-['Barlow_Condensed'] uppercase tracking-tight mb-6">
              Fale com um Especialista
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div>
                <Label htmlFor="nome" className="text-sm font-semibold text-[#334155] font-['Manrope']">
                  Nome Completo *
                </Label>
                <Input
                  id="nome"
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  required
                  placeholder="Seu nome"
                  data-testid="contact-input-nome"
                  className="mt-1 bg-gray-50 border-gray-200 focus:border-[#0E86D4] focus:ring-1 focus:ring-[#0E86D4] rounded-sm"
                />
              </div>

              <div>
                <Label htmlFor="telefone" className="text-sm font-semibold text-[#334155] font-['Manrope']">
                  Telefone / WhatsApp *
                </Label>
                <Input
                  id="telefone"
                  name="telefone"
                  value={form.telefone}
                  onChange={handleChange}
                  required
                  placeholder="(62) 99999-9999"
                  data-testid="contact-input-telefone"
                  className="mt-1 bg-gray-50 border-gray-200 focus:border-[#0E86D4] focus:ring-1 focus:ring-[#0E86D4] rounded-sm"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-[#334155] font-['Manrope']">
                  E-mail
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="seu@email.com"
                  data-testid="contact-input-email"
                  className="mt-1 bg-gray-50 border-gray-200 focus:border-[#0E86D4] focus:ring-1 focus:ring-[#0E86D4] rounded-sm"
                />
              </div>

              <div>
                <Label htmlFor="servico" className="text-sm font-semibold text-[#334155] font-['Manrope']">
                  Serviço Desejado *
                </Label>
                <select
                  id="servico"
                  name="servico"
                  value={form.servico}
                  onChange={handleChange}
                  required
                  data-testid="contact-select-servico"
                  className="mt-1 w-full h-10 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-sm focus:border-[#0E86D4] focus:ring-1 focus:ring-[#0E86D4] outline-none font-['Manrope']"
                >
                  <option value="">Selecione um serviço</option>
                  {SERVICES_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="mensagem" className="text-sm font-semibold text-[#334155] font-['Manrope']">
                  Mensagem
                </Label>
                <textarea
                  id="mensagem"
                  name="mensagem"
                  value={form.mensagem}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Descreva brevemente o que precisa..."
                  data-testid="contact-textarea-mensagem"
                  className="mt-1 w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-sm focus:border-[#0E86D4] focus:ring-1 focus:ring-[#0E86D4] outline-none resize-none font-['Manrope']"
                />
              </div>

              <Button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-btn"
                className="w-full bg-[#CC1B1B] hover:bg-[#991B1B] text-white font-['Barlow_Condensed'] uppercase tracking-wider text-base font-bold rounded-sm py-6 shadow-lg hover:shadow-xl transition-all"
              >
                <Send className="w-5 h-5 mr-2" />
                {sending ? "Enviando..." : "Enviar e Falar no WhatsApp"}
              </Button>

              <p className="text-xs text-center text-[#64748B] font-['Manrope'] mt-2">
                * Suas informações estão seguras conosco. Ao enviar, você será redirecionado para o WhatsApp.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
